// Thin client wrappers for the Settings + Personal Settings surfaces (Team,
// customer MFA, tenant settings, Usage). All go through apiFetch, which ships
// the crauto-auth cookie and maps non-2xx to ApiError.

import { toDataSourceRequestString } from '@progress/kendo-data-query';
import type { State } from '@progress/kendo-data-query';
import type {
  AccountManager,
  AddTeamMemberRequest,
  AddTeamMemberResponse,
  ChangePasswordRequest,
  CurrentUsage,
  DataSourceResultDto,
  GenerateIntegrationKeyResponse,
  IntegrationKeyStatus,
  IntegrationProduct,
  MfaEnrollBeginResponse,
  MfaEnrollCompleteRequest,
  MfaEnrollCompleteResponse,
  ResetTeamMemberPasswordResponse,
  TeamMember,
  TenantSettings,
  UpdateTeamMemberRequest,
  UpdateTenantSettingsRequest,
  UsageHistoryPoint,
  UsageLogRow,
} from '@shared/api-types';
import { apiFetch } from './apiClient';

// --- Team -------------------------------------------------------------------

export const listTeamMembers = () => apiFetch<TeamMember[]>('/api/team/members');

export const addTeamMember = (body: AddTeamMemberRequest) =>
  apiFetch<AddTeamMemberResponse>('/api/team/members', { method: 'POST', body });

export const updateTeamMember = (userId: string, body: UpdateTeamMemberRequest) =>
  apiFetch<TeamMember>(`/api/team/members/${userId}`, { method: 'PUT', body });

export const removeTeamMember = (userId: string) =>
  apiFetch<void>(`/api/team/members/${userId}`, { method: 'DELETE' });

export const clearEmailSuspension = (userId: string) =>
  apiFetch<TeamMember>(`/api/team/members/${userId}/clear-suspension`, { method: 'POST' });

/** Reset a teammate's password to a fresh one-time temp password (returned once, also emailed). */
export const resetTeamMemberPassword = (userId: string) =>
  apiFetch<ResetTeamMemberPasswordResponse>(`/api/team/members/${userId}/reset-password`, { method: 'POST' });

// --- Customer MFA -----------------------------------------------------------

export const beginMfaEnroll = () =>
  apiFetch<MfaEnrollBeginResponse>('/api/me/mfa/enroll/begin', { method: 'POST' });

export const completeMfaEnroll = (body: MfaEnrollCompleteRequest) =>
  apiFetch<MfaEnrollCompleteResponse>('/api/me/mfa/enroll/complete', { method: 'POST', body });

export const disableMfa = () => apiFetch<void>('/api/me/mfa/disable', { method: 'POST' });

/** Clear the login MFA gate with a TOTP or single-use recovery code. */
export const verifyMfa = (code: string) =>
  apiFetch<void>('/api/me/mfa/verify', { method: 'POST', body: { code } });

export const changePassword = (body: ChangePasswordRequest) =>
  apiFetch<void>('/api/me/password', { method: 'POST', body });

// --- Tenant settings --------------------------------------------------------

export const getTenantSettings = () => apiFetch<TenantSettings>('/api/me/tenant/settings');

export const updateTenantSettings = (body: UpdateTenantSettingsRequest) =>
  apiFetch<TenantSettings>('/api/me/tenant/settings', { method: 'PUT', body });

/** The tenant's CloudRadial account manager (Settings → Overview banner). Readable by every member. */
export const getAccountManager = () => apiFetch<AccountManager>('/api/me/tenant/account-manager');

// --- Usage ------------------------------------------------------------------

export const getCurrentUsage = () => apiFetch<CurrentUsage>('/api/usage/current');

export const getUsageHistory = (months: number) =>
  apiFetch<UsageHistoryPoint[]>(`/api/usage/history?months=${months}`);

export const getUsageLog = (state: State, signal?: AbortSignal) =>
  apiFetch<DataSourceResultDto<UsageLogRow>>(
    `/api/usage/log?${toDataSourceRequestString(state)}`,
    { signal },
  );

// --- Integration keys (one per integrating product) --------------------------
//
// Product-scoped: each call names the product in the route and sees only that
// product's key. `api/settings/serviceai-key` survives on the backend as a
// ServiceAi-fixed alias for callers that predate the product routes; the app
// goes through the product routes for every product, ServiceAI included.

const integrationKeyPath = (product: IntegrationProduct) =>
  `/api/settings/integration-keys/${product}`;

export const getIntegrationKey = (product: IntegrationProduct) =>
  apiFetch<IntegrationKeyStatus>(integrationKeyPath(product));

/** Generate (or regenerate) one product's key. The plaintext is returned once, here only. */
export const generateIntegrationKey = (product: IntegrationProduct) =>
  apiFetch<GenerateIntegrationKeyResponse>(integrationKeyPath(product), { method: 'POST' });

export const revokeIntegrationKey = (product: IntegrationProduct) =>
  apiFetch<void>(integrationKeyPath(product), { method: 'DELETE' });
